import { useState, type ImgHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

interface MediaImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | undefined | null;
  alt: string;
  ratio?: 'square' | 'video' | 'portrait' | 'wide' | 'auto';
  placeholder?: string;
  /** Cloudflare Image Resizing hints (safe no-ops on non-CF hosts). */
  cfWidth?: number;
  cfFit?: 'cover' | 'contain' | 'crop';
}

const RATIO_CLASS: Record<NonNullable<MediaImageProps['ratio']>, string> = {
  square: 'aspect-square',
  video: 'aspect-video',
  portrait: 'aspect-[3/4]',
  wide: 'aspect-[21/9]',
  auto: '',
};

/**
 * Renders an <img> with blur / shimmer placeholder + lazy loading.
 * Skips render when `src` is falsy so callers can fall back to a placeholder container.
 */
export function MediaImage({
  src,
  alt,
  className,
  ratio = 'auto',
  placeholder,
  cfWidth,
  cfFit = 'cover',
  ...rest
}: MediaImageProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const resolved = src ? withCfImage(src, cfWidth, cfFit) : undefined;

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-muted',
        RATIO_CLASS[ratio],
        className,
      )}
    >
      {!resolved || errored ? (
        <div
          role="img"
          aria-label={alt}
          className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-muted to-accent text-xs uppercase tracking-widest text-muted-foreground"
        >
          {placeholder ?? 'Image coming soon'}
        </div>
      ) : (
        <>
          <div
            aria-hidden
            className={cn(
              'absolute inset-0 animate-shimmer bg-gradient-to-r from-muted via-accent/40 to-muted bg-[length:200%_100%] transition-opacity duration-500',
              loaded && 'opacity-0',
            )}
          />
          <img
            src={resolved}
            alt={alt}
            loading="lazy"
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className={cn(
              'h-full w-full object-cover transition-opacity duration-700',
              loaded ? 'opacity-100' : 'opacity-0',
            )}
            {...rest}
          />
        </>
      )}
    </div>
  );
}

/**
 * If the URL is served from Cloudflare, pass through `/cdn-cgi/image/...` hints.
 * On other hosts the URL is returned unchanged (no error, just no optimization).
 */
function withCfImage(url: string, width?: number, fit: 'cover' | 'contain' | 'crop' = 'cover') {
  if (!width) return url;
  try {
    const u = new URL(url);
    const hints = [`width=${width}`, `fit=${fit}`, 'format=auto', 'quality=82'].join(',');
    return `${u.origin}/cdn-cgi/image/${hints}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
}
